import React from 'react';

const PlatformDistribution = ({ data, isSingleCity }) => {
  const platforms = ['Google Reviews', 'App Store', 'Play Store', 'Amazon'];
  const platformCounts = platforms.map(platform => {
    const count = data.filter(review => review.source === platform).length;
    const percentage = data.length > 0 ? Math.round((count / data.length) * 100) : 0;
    return { platform, count, percentage };
  });
  
  // Sort platforms by count (descending)
  platformCounts.sort((a, b) => b.count - a.count);
  
  // Calculate max count for proportional bars
  const maxCount = Math.max(...platformCounts.map(p => p.count || 1));
  
  // Colors for each platform
  const platformColors = {
    'Google Reviews': '#4285F4',
    'App Store': '#34A853',
    'Play Store': '#FBBC05',
    'Amazon': '#FF9900'
  };
  
  // SVG Pie Chart (for single city view)
  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  
  // Calculate stroke dasharray values and offsets for each segment
  let currentOffset = 0;
  const pieSegments = platformCounts.map(platform => {
    const dash = (platform.percentage / 100) * circumference;
    const segment = {
      dash,
      offset: currentOffset,
      color: platformColors[platform.platform],
      platform: platform.platform,
      percentage: platform.percentage
    };
    currentOffset += dash;
    return segment;
  });
  
  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-lg font-medium mb-4">Platform Distribution</h2>
      {isSingleCity ? (
        // Pie chart view for single city
        <div className="flex justify-center">
          <div className="relative w-40 h-40">
            <svg viewBox="0 0 200 200" className="transform -rotate-90">
              {/* Background circle */}
              <circle 
                cx="100" 
                cy="100" 
                r={radius} 
                fill="none" 
                stroke="#f3f4f6" 
                strokeWidth="20"
              />
              
              {/* Platform segments */}
              {pieSegments.map((segment, index) => segment.dash > 0 && (
                <circle 
                  key={index}
                  cx="100" 
                  cy="100" 
                  r={radius} 
                  fill="none" 
                  stroke={segment.color} 
                  strokeWidth="20"
                  strokeDasharray={`${segment.dash} ${circumference - segment.dash}`} 
                  strokeDashoffset={-segment.offset} 
                />
              ))}
            </svg>
            
            {/* Total in the center */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-3xl font-bold">{data.length}</div>
                <div className="text-xs text-gray-500">Total</div>
              </div>
            </div>
          </div>
          
          {/* Legend */}
          <div className="flex flex-col justify-center ml-6">
            {platformCounts.map((platform, index) => platform.count > 0 && (
              <div key={index} className="flex items-center mb-2">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: platformColors[platform.platform] }}></div>
                <span className="text-sm ml-2">{platform.platform} ({platform.percentage}%)</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        // Bar chart view for global/region
        <div className="space-y-4">
          {platformCounts.map((platform, index) => (
            <div key={index} className="flex flex-col">
              <div className="flex justify-between mb-1">
                <div className="flex items-center">
                  <span 
                    className="w-3 h-3 rounded-sm mr-2" 
                    style={{ backgroundColor: platformColors[platform.platform] }}
                  ></span>
                  <span className="text-sm font-medium">{platform.platform}</span>
                </div>
                <div className="text-sm text-gray-500">{platform.count} ({platform.percentage}%)</div>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full"
                  style={{ 
                    width: maxCount > 0 ? `${(platform.count / maxCount) * 100}%` : '0%',
                    backgroundColor: platformColors[platform.platform]
                  }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Platform metrics summary */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-2 pt-4 border-t border-gray-200">
        {platformCounts.map((platform, index) => (
          <div key={index} className="text-center">
            <div className="text-xs text-gray-500">{platform.platform}</div>
            <div className="text-lg font-bold mt-1">{platform.count}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlatformDistribution;